import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User } from 'lucide-react';
interface ArticleHeroProps {
  badge?: string;
  title: string;
  highlightedWord?: string;
  subtitle: string;
  author?: string;
  readTime?: string;
}
export function ArticleHero({
  badge = 'Canadian Casino Guide',
  title,
  highlightedWord,
  subtitle,
  author = 'Peter K.',
  readTime = '8 min read'
}: ArticleHeroProps) {
  const currentDate = new Date();
  const currentMonth = currentDate.toLocaleString('en-US', {
    month: 'short'
  });
  const currentYear = currentDate.getFullYear();
  // Split title by highlighted word if provided
  const renderTitle = () => {
    if (!highlightedWord) {
      return <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight" data-id="element-9">
          {title}
        </h1>;
    }
    const parts = title.split(highlightedWord);
    return <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight" data-id="element-10">
        {parts[0]}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-200" data-id="element-11">
          {highlightedWord}
        </span>
        {parts[1]}
      </h1>;
  };
  return <div className="relative w-full bg-slate-900 overflow-hidden" data-id="element-12">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10" data-id="element-13">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-overlay" data-id="element-14"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-purple-900/50 to-slate-900" data-id="element-15"></div>
      </div>

      {/* Decorative Glows */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-16"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-17"></div>

      <div className="relative max-w-4xl mx-auto px-6 pt-24 pb-20 text-center" data-id="element-18">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-19">
          <span className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-sm font-bold tracking-wide uppercase mb-6 shadow-lg shadow-yellow-500/20" data-id="element-20">
            {badge}
          </span>

          {renderTitle()}

          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-21">
            {subtitle}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6 text-slate-400 text-sm font-medium" data-id="element-22">
            <div className="flex items-center gap-2" data-id="element-23">
              <User className="w-4 h-4 text-purple-400" data-id="element-24" />
              <span data-id="element-25">By {author}</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-26">
              <Calendar className="w-4 h-4 text-purple-400" data-id="element-27" />
              <span data-id="element-28">
                Updated {currentMonth} {currentYear}
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-29">
              <Clock className="w-4 h-4 text-purple-400" data-id="element-30" />
              <span data-id="element-31">{readTime}</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom fade to content */}
      <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-slate-50 to-transparent" data-id="element-32"></div>
    </div>;
}